import React from 'react';
import { TaskUser } from '../../types/tasks';
import { userColor, userInitial, userName } from '../../utils/taskUsers';
import TaskIcon from './TaskIcon';

/** The small initial-in-a-circle that marks whose responsibility a line is. */
export const UserBubble: React.FC<{
  users: TaskUser[];
  email: string | null | undefined;
  size?: 'sm' | 'md';
  title?: string;
}> = ({ users, email, size = 'sm', title }) => {
  if (!email) return null;
  return (
    <span
      className={`tk-bubble tk-bubble-${size}`}
      style={{ background: userColor(users, email) }}
      title={title ?? userName(users, email)}
    >
      {userInitial(users, email)}
    </span>
  );
};

/** Several people's bubbles, overlapped. Shows `max` and a "+n" after that. */
export const BubbleStack: React.FC<{ users: TaskUser[]; emails: string[]; max?: number }> = ({ users, emails, max = 3 }) => {
  const shown = emails.slice(0, max);
  const extra = emails.length - shown.length;
  return (
    <span className="tk-stack">
      {shown.map((email) => (
        <UserBubble key={email} users={users} email={email} title="" />
      ))}
      {extra > 0 && <span className="tk-bubble tk-bubble-sm tk-bubble-more">+{extra}</span>}
    </span>
  );
};

/**
 * Pick who's responsible. Multi-select: each tap toggles a person and the
 * picker stays open; "Nobody" clears the lot.
 */
export const UserPicker: React.FC<{
  users: TaskUser[];
  value: string[];
  me: string;
  onChange: (emails: string[]) => void;
  hint?: string;
}> = ({ users, value, me, onChange, hint }) => {
  const sorted = [...users].sort((a, b) =>
    a.email === me ? -1 : b.email === me ? 1 : userName(users, a.email).localeCompare(userName(users, b.email))
  );
  const toggle = (email: string) =>
    onChange(value.includes(email) ? value.filter((e) => e !== email) : [...value, email]);
  return (
    <div className="tk-user-picker" role="group" aria-label="Assign people">
      <button type="button" className={`tk-menu-item${value.length === 0 ? ' is-selected' : ''}`} onClick={() => onChange([])}>
        <span className="tk-bubble tk-bubble-sm tk-bubble-empty"><TaskIcon name="user" size={11} /></span>
        <span>Nobody</span>
        {value.length === 0 && <TaskIcon name="check" size={14} className="tk-menu-check" />}
      </button>
      {sorted.map((u) => {
        const on = value.includes(u.email);
        return (
          <button
            type="button"
            key={u.email}
            className={`tk-menu-item${on ? ' is-selected' : ''}`}
            onClick={() => toggle(u.email)}
            role="checkbox"
            aria-checked={on}
          >
            <UserBubble users={users} email={u.email} />
            <span>
              {userName(users, u.email)}
              {u.email === me && <span className="tk-muted"> (you)</span>}
            </span>
            <span className={`tk-tick${on ? ' is-on' : ''}`} aria-hidden="true">
              {on && <TaskIcon name="check" size={12} />}
            </span>
          </button>
        );
      })}
      {hint && <p className="tk-menu-hint">{hint}</p>}
    </div>
  );
};
